import React from "react";
import { createRoot } from "react-dom/client";
import { addInitialFunction, Context } from "./dfm5";
import ViewCourses from "~/components/pages/view-courses/ViewCourses";
import {
  getPublishedCourses,
  getRecentlyViewedCourses,
  getSchoolCourses,
  getUserCoursesByClass,
} from "~/utils/courses";
import { posthogPageviewEvent } from "~/utils/posthog/general";

const root = createRoot(document.getElementById("react-root"));

/**
 * @typedef {Object} CourseGroup
 * @property {{
 *  authorName: string,
 *  coid: number,
 *  name: string,
 *  numberOfModules: number | undefined,
 *  thumb: string,
 *  type: string
 * }[]} courses
 * @property {number | undefined} defaultDisplayCount
 * @property {string} description
 * @property {boolean | undefined} displayManageCoursesLink
 * @property {string} title
 * @property {"class" | "school" | "popular" | "recent"} type
 */

/**
 * Builds course groups for each class the user belongs to that
 * has courses assigned.
 *
 * @param {{ uid: number }} user
 * @returns {Promise<CourseGroup[]>}
 */
async function buildClassGroups(user) {
  const classGroups = [];

  const coursesByClass = (await getUserCoursesByClass(user.uid)) ?? [];

  coursesByClass.forEach(({ courses = [], className }) => {
    if (courses.length && className) {
      classGroups.push({
        description: `Courses that have been assigned to ${className}`,
        courses,
        title: `Class ${className}`,
        type: "class",
      });
    }
  });

  return classGroups;
}

/**
 * Builds a course group for the school the user belongs to, if any.
 * If the user is a student and the school has no courses, reutrn null.
 *
 * @param {{ sid: number, type: string, _school: { name: string } }} user
 * @returns {Promise<CourseGroup | null>}
 */
async function buildSchoolGroup(user) {
  if (!user.sid) {
    return null;
  }

  const showClasses = ["admin", "teacher", "trust"].includes(user.type);

  const courses = (await getSchoolCourses({ getClasses: showClasses, sid: user.sid })) ?? [];
  const validCourses = courses.filter(
    (course) => course.status === 1 || course.status === 3,
  );

  if (validCourses.length === 0 && user.type === "student") {
    return null;
  }

  const description =
    user.type === "student"
      ? "Courses that have been created or selected by your teachers"
      : "Courses that your school has created or selected";

  return {
    description,
    displayManageCoursesLink: ["admin", "teacher", "trust"].includes(user.type),
    courses: validCourses,
    showClasses,
    title: user._school?.name ?? "Your school",
    type: "school",
  };
}

/**
 * Builds a course group for a selection of the most
 * popular courses on the platform.
 *
 * @returns {Promise<CourseGroup | null>}
 */
async function buildMostPopularGroup() {
  /**
   * Hard coding a custom selection of published courses for now.
   *
   * TODO: We will need a way to customise the content on this
   * page in general, including this row.
   */
  let coids = [];
  if (process.env.NODE_ENV === "development") {
    // A selection of coids from the seed data.
    coids = [
      13047, // WRM Year 7
      12909, // Edexcel GCSE Higher
      12896, // AQA L2 Further Maths
    ]
  } else {
    coids = [
      13005, // WRM Year 7
      13006, // WRM Year 8
      13007, // WRM Year 9
      12808, // Edexcel GCSE Foundation
      12809, // Edexcel GCSE Higher
      12796, // AQA L2 Further Maths
      12814, // Edexcel AS Level
      12815, // Edexcel A Level
    ];
  }
  const courses = (await getPublishedCourses(coids)) ?? [];

  if (courses.length) {
    return {
      description: "A selection of the most popular courses on Dr Frost",
      courses,
      title: "Most popular",
      type: "popular",
    };
  }

  return null;
}

/**
 * Builds a course group for the user's recently viewed courses.
 *
 * @param {{ uid: number }} user
 * @returns {Promise<CourseGroup | null>}
 */
async function buildRecentlyViewedGroup(user) {
  // Get the recent courses from local storage.
  const courses = getRecentlyViewedCourses(user.uid) ?? [];


  if (courses.length) {
    return {
      description: "Courses that you have recently viewed",
      courses,
      title: "Recently viewed",
      type: "recent",
    };
  }

  return null;
}

async function renderPage(user) {
  // Set the subtitle based on the user type.
  const pageSubtitle =
    user.type === "student"
      ? "Courses are collections of skills organised into units and modules, designed to guide your learning. Click on a course to explore its content, practice the skills, and start answering questions."
      : "Organise Dr Frost content in a way that is suitable for your students";

  // Render the page with a loading state.
  root.render(<ViewCourses isLoading={true} subtitle={pageSubtitle} />);

  // Build the course groups array.
  const promises = [
    user.type === "student" ? buildClassGroups(user) : Promise.resolve([]),
    buildSchoolGroup(user),
    buildRecentlyViewedGroup(user),
    buildMostPopularGroup(),
  ];
  const [classGroups, schoolGroup, recentlyViewedGroup, mostPopularGroup] =
    await Promise.all(promises);
  const groups = [
    ...classGroups,
    ...(schoolGroup ? [schoolGroup] : []),
    ...(recentlyViewedGroup ? [recentlyViewedGroup] : []),
    ...(mostPopularGroup ? [mostPopularGroup] : []),
  ];

  // Render the page with the loaded course groups.
  root.render(
    <ViewCourses
      groups={groups}
      isLoading={false}
      subtitle={pageSubtitle}
      user={user}
    />,
  );
}

async function renderPageForGuest() {
  // Set the subtitle for guests.
  const pageSubtitle = "Courses are collections of skills organised into units and modules. Click on a course to explore its content.";

  // Render the page with a loading state.
  root.render(<ViewCourses isLoading={true} subtitle={pageSubtitle} />);

  // Build the most popular courses groups.
  const mostPopularGroup = await buildMostPopularGroup();

  // Render the page with the loaded course groups.
  root.render(
    <ViewCourses
      groups={mostPopularGroup ? [mostPopularGroup] : []}
      isLoading={false}
      subtitle={pageSubtitle}
    />,
  );
}

$(() => {
  // Send a page view event when Context is available.
  addInitialFunction(posthogPageviewEvent);

  // Also send a page view event if we've navigated here via the bfcache
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      posthogPageviewEvent();
    }
  });

  addInitialFunction(() => {
    const user = Context?.user;
    if (!user) {
      renderPageForGuest();
    } else {
      renderPage(user);
    }
  });
});
